import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { inspectionsApi, templatesApi, assetsApi } from '../services/api';
import { DensityToggle, SortableTh, useDensity, useSortState } from '../components/ui/list-controls';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import SignaturePad from 'signature_pad';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { SlideDrawer } from '../components/ui/slide-drawer';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { 
  isToday, 
  isThisWeek, 
  isThisMonth, 
  startOfWeek, 
  endOfWeek, 
  subWeeks, 
  startOfMonth, 
  endOfMonth, 
  subMonths,
  startOfDay,
  endOfDay,
  isWithinInterval,
  parseISO
} from 'date-fns';
import { 
  ArrowLeft, 
  Loader2, 
  ClipboardCheck, 
  Camera, 
  MapPin,
  Check,
  X,
  Minus,
  PenTool,
  AlertTriangle,
  Eye,
  Search,
  Package,
  Trash2,
  Link as LinkIcon,
  QrCode,
  Download,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const DATE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This Week' },
  { id: 'last_week', label: 'Last Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
];

// Map component for GPS location
const LocationMap = ({ lat, lng }) => {
  if (!lat || !lng) return null;
  
  // Using OpenStreetMap static image
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.01},${lat-0.01},${lng+0.01},${lat+0.01}&layer=mapnik&marker=${lat},${lng}`;
  
  return (
    <div className="space-y-2">
      <Label className="text-sm text-muted-foreground flex items-center gap-2">
        <MapPin className="w-4 h-4" />
        Location
      </Label>
      <div className="rounded-lg overflow-hidden border border-border">
        <iframe
          src={mapUrl}
          width="100%"
          height="200"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          title="Inspection Location"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Coordinates: {lat.toFixed(6)}, {lng.toFixed(6)}
      </p>
      <a 
        href={`https://www.google.com/maps?q=${lat},${lng}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-primary hover:underline"
      >
        Open in Google Maps →
      </a>
    </div>
  );
};

// Inspection Detail View Component (for SlideDrawer)
const InspectionDetailView = ({ inspection: initialInspection, onClose, onDelete, isAdmin }) => {
  const [inspection, setInspection] = useState(initialInspection);
  const [template, setTemplate] = useState(null);
  const [asset, setAsset] = useState(null);
  const [auxiliaries, setAuxiliaries] = useState([]);  // linked aux assets + their last inspection
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const { formatDateTime } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    loadDetails();
  }, [initialInspection?.id]);

  const loadDetails = async () => {
    if (!initialInspection) return;
    try {
      // Fetch full inspection details (including responses), template, and asset in parallel
      const [inspectionRes, templateRes, assetRes] = await Promise.all([
        inspectionsApi.getById(initialInspection.id),  // Get full inspection with responses
        templatesApi.getById(initialInspection.template_id),
        assetsApi.getById(initialInspection.asset_id),
      ]);
      setInspection(inspectionRes.data);  // Use full inspection data
      setTemplate(templateRes.data);
      setAsset(assetRes.data);

      // Pull every auxiliary asset linked to this asset, plus each one's
      // most recent inspection. Lets the mechanic see at a glance whether
      // the trailer/slings that ride with this unit are also signed off.
      try {
        const allAssetsRes = await assetsApi.getAll();
        const linkedAux = (allAssetsRes.data || []).filter(
          (a) => a.parent_asset_id === initialInspection.asset_id && a.asset_class === 'auxiliary'
        );
        if (linkedAux.length) {
          const auxInspectionsRes = await inspectionsApi.getAll();
          const auxInspections = auxInspectionsRes.data || [];
          const byAsset = {};
          for (const ai of auxInspections) {
            const cur = byAsset[ai.asset_id];
            if (!cur || new Date(ai.completed_at || ai.created_at) > new Date(cur.completed_at || cur.created_at)) {
              byAsset[ai.asset_id] = ai;
            }
          }
          setAuxiliaries(linkedAux.map((a) => ({ ...a, last_inspection: byAsset[a.id] || null })));
        } else {
          setAuxiliaries([]);
        }
      } catch {
        setAuxiliaries([]); // non-fatal — main inspection display still works
      }
    } catch (error) {
      console.error('Failed to load inspection details');
    } finally {
      setLoading(false);
    }
  };

  const resultTone = (result) => {
    if (result === 'pass') return { cls: 'id-check', icon: '#059669' };
    if (result === 'fail') return { cls: 'id-fail', icon: '#DC2626' };
    return { cls: 'id-neutral', icon: '#8A857A' };
  };

  const resultIcon = (result, color) => {
    if (result === 'pass') return <Check className="w-5 h-5 shrink-0 mt-0.5" style={{ color }} />;
    if (result === 'fail') return <X className="w-5 h-5 shrink-0 mt-0.5" style={{ color }} />;
    return <Minus className="w-5 h-5 shrink-0 mt-0.5" style={{ color }} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!inspection) return null;

  const checklistMap = template?.checklist?.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {}) || {};

  const passedCount = inspection.responses?.filter(r => r.result === 'pass').length || 0;
  const failedCount = inspection.responses?.filter(r => r.result === 'fail').length || 0;
  const totalCount = inspection.responses?.length || 0;

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await inspectionsApi.downloadPdf(inspection.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `inspection-${(inspection.template_name || 'report').replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not generate PDF'); }
    finally { setDownloadingPdf(false); }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {inspection.template_name || template?.name || 'Inspection'}
          </h2>
          <p className="text-sm text-[#59554D] mt-1">
            Performed by <span className="font-semibold text-[#1A1A1A]">{inspection.inspector_name}</span> on{' '}
            <span className="font-mono text-[#1A1A1A]">{formatDateTime(inspection.created_at)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloadingPdf} data-testid="inspection-download-pdf">
            <Download className="w-3.5 h-3.5 mr-1.5" />{downloadingPdf ? 'Preparing…' : 'Download PDF'}
          </Button>
          {(inspection.flags || []).includes('unusual_hours_reading') && (
            <span
              className="lm-warn inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border"
              title="A measurement on this inspection was unusually high vs the asset's previous value. Review before relying on it."
              data-testid="unusual-reading-badge"
            >
              ⚠ Unusual reading
            </span>
          )}
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
              inspection.status === 'passed' ? 'lm-ok' : 'lm-crit'
            }`}
            data-testid="inspection-status-pill"
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: inspection.status === 'passed' ? '#047857' : '#DC2626' }} />
            {inspection.status}
          </span>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { n: passedCount, label: 'Passed', color: '#047857' },
          { n: failedCount, label: 'Failed', color: failedCount > 0 ? '#DC2626' : '#1A1A1A' },
          { n: totalCount, label: 'Total', color: '#1A1A1A' },
        ].map((t) => (
          <div key={t.label} className="id-tile flex flex-col items-center justify-center px-4 py-7 text-center" data-testid={`inspection-summary-${t.label.toLowerCase()}`}>
            <div className="font-bold leading-none" style={{ fontFamily: 'Manrope, sans-serif', fontSize: 34, color: t.color }}>{t.n}</div>
            <div className="text-[13px] text-[#8A857A] mt-2.5">{t.label}</div>
          </div>
        ))}
      </div>

      {/* Asset */}
      {asset && (
        <div className="id-card p-4 flex items-center gap-3" data-testid="inspection-asset-card">
          <div className="w-11 h-11 rounded-[10px] bg-[#F4F2ED] border border-[#E6E2D9] flex items-center justify-center shrink-0">
            <Package className="w-5 h-5 text-[#59554D]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A]">Asset</div>
            <p className="font-bold text-[#1A1A1A] leading-tight">{asset.unit_number || asset.name}</p>
            <p className="text-xs font-mono text-[#8A857A]">{asset.serial_number}</p>
          </div>
          <button
            type="button"
            onClick={() => { onClose?.(); navigate(`/assets?view=${asset.id}`); }}
            className="ml-auto shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-[#59554D] hover:text-[#1A1A1A] transition-colors"
            data-testid="inspection-open-asset"
          >
            Open asset <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Location */}
      {inspection.gps_lat && inspection.gps_lng && (
        <div className="id-card p-4" data-testid="inspection-location-card">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A]">
              <MapPin className="w-4 h-4 text-[#C9A400]" />
              Location
            </div>
            <span className="text-[11px] font-mono text-[#8A857A]">
              Coordinates: {inspection.gps_lat.toFixed(6)}, {inspection.gps_lng.toFixed(6)}
            </span>
          </div>
          <div className="rounded-lg overflow-hidden border border-[#E6E2D9]">
            <iframe
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${inspection.gps_lng - 0.01},${inspection.gps_lat - 0.01},${inspection.gps_lng + 0.01},${inspection.gps_lat + 0.01}&layer=mapnik&marker=${inspection.gps_lat},${inspection.gps_lng}`}
              width="100%"
              height="220"
              style={{ border: 0 }}
              loading="lazy"
              title="Inspection Location"
            />
          </div>
          <a
            href={`https://www.google.com/maps?q=${inspection.gps_lat},${inspection.gps_lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-3 text-sm font-semibold text-[#B45309] hover:underline"
          >
            Open in Google Maps →
          </a>
        </div>
      )}

      {/* Linked auxiliaries */}
      {auxiliaries.length > 0 && (
        <div className="id-card p-4" data-testid="inspection-aux-rollup">
          <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A]">
            <LinkIcon className="w-4 h-4 text-[#C9A400]" />
            Linked auxiliaries ({auxiliaries.length})
          </div>
          <p className="text-xs text-[#59554D] mt-2">
            {inspection.passed === false || (inspection.failed_count || 0) > 0 ? (
              <span className="text-[#B91C1C]">
                This inspection failed — every auxiliary attached to {asset?.unit_number || 'this asset'} is implicitly blocked from use until the parent is signed off.
              </span>
            ) : (
              'All auxiliaries below need their own current inspection before they can be released.'
            )}
          </p>
          <div className="space-y-2 mt-3">
            {auxiliaries.map((a) => {
              const parentBlocked = inspection.passed === false || (inspection.failed_count || 0) > 0;
              const li = a.last_inspection;
              const liPassed = li?.passed === true || (li?.failed_count ?? 1) === 0;
              const liStale = li && new Date(li.completed_at || li.created_at) < new Date(Date.now() - 30 * 86400000);
              const bad = parentBlocked || (li && !liPassed);
              const warn = !li || liStale;
              const cardCls = bad ? 'id-fail' : warn ? 'id-warn' : 'id-check';
              const textColor = bad ? '#B91C1C' : warn ? '#B45309' : '#047857';
              return (
                <div key={a.id} className={`flex items-start gap-3 p-3.5 ${cardCls}`} data-testid={`inspection-aux-${a.id}`}>
                  <Package className="w-4 h-4 text-[#59554D] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-[#1A1A1A]">{a.unit_number || a.name}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#F4F2ED] text-[#59554D] border border-[#E6E2D9]">{a.category}</span>
                    </div>
                    <div className="text-[11px] font-mono mt-0.5" style={{ color: textColor }}>
                      {parentBlocked ? '⛔ Blocked by parent inspection failure'
                        : !li ? '⚠ No inspection on record'
                        : !liPassed ? `✗ Last inspection failed · ${formatDateTime(li.completed_at || li.created_at)}`
                        : liStale ? `⚠ Passed but >30d old · ${formatDateTime(li.completed_at || li.created_at)}`
                        : `✓ Passed · ${formatDateTime(li.completed_at || li.created_at)}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Checklist Results */}
      <div className="id-card p-5" data-testid="inspection-checklist-card">
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A] mb-4">Checklist Results</div>
        <div className="flex flex-col gap-3">
          {inspection.responses?.map((response, index) => {
            const item = checklistMap[response.item_id];
            const question = item?.question || `Item ${index + 1}`;
            const itemType = item?.item_type || 'pass_fail';
            const isMeasurement = itemType !== 'pass_fail';

            let measurementDisplay = null;
            if (itemType === 'hours' || itemType === 'odometer' || itemType === 'number') {
              const unit = item?.unit || (itemType === 'hours' ? 'hrs' : itemType === 'odometer' ? 'km' : '');
              measurementDisplay = response.value_number != null ? `${response.value_number}${unit ? ' ' + unit : ''}` : '—';
            } else if (itemType === 'text') {
              measurementDisplay = response.value_text || '—';
            } else if (itemType === 'select') {
              measurementDisplay = response.value_select || '—';
            } else if (itemType === 'signature') {
              measurementDisplay = response.signature ? 'Signed' : '—';
            }

            const tone = isMeasurement ? resultTone('na') : resultTone(response.result);
            return (
              <div key={response.item_id || index} className={`p-4 ${tone.cls}`}>
                <div className="flex items-start gap-3">
                  {isMeasurement ? <Minus className="w-5 h-5 shrink-0 mt-0.5" style={{ color: tone.icon }} /> : resultIcon(response.result, tone.icon)}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[15px] text-[#1A1A1A]">{question}</p>
                    {isMeasurement && (
                      <p className="text-sm font-mono mt-1" style={{ color: '#B45309' }}>{measurementDisplay}</p>
                    )}
                    {response.comment && (
                      <p className="text-sm text-[#59554D] mt-1">{response.comment}</p>
                    )}
                    {response.photo && (
                      <img
                        src={response.photo.startsWith('data:') ? response.photo : `data:image/jpeg;base64,${response.photo}`}
                        alt="Response"
                        className="mt-2.5 rounded-lg border border-[#E6E2D9] max-h-36 object-cover bg-white"
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Signature */}
      {inspection.signature && (
        <div className="id-card p-4" data-testid="inspection-signature-card">
          <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A] mb-3">
            <PenTool className="w-4 h-4 text-[#59554D]" />
            Signature
          </div>
          <div className="rounded-lg border border-[#E6E2D9] bg-white p-2 flex justify-center">
            <img src={inspection.signature} alt="Inspector Signature" className="max-h-24" />
          </div>
        </div>
      )}

      {isAdmin && onDelete && (
        <div className="pt-1">
          <button
            type="button"
            onClick={onDelete}
            className="w-full h-12 rounded-lg bg-[#DC2626] hover:bg-[#B91C1C] text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors"
            data-testid="inspection-delete-btn"
          >
            <Trash2 className="w-4 h-4" />
            Delete inspection
          </button>
          <p className="text-[11px] text-[#8A857A] text-center mt-2">
            Deletes this inspection record. Defects raised from it remain in your register.
          </p>
        </div>
      )}
    </div>
  );
};

export const InspectionsListPage = () => {
  const { isAdmin } = useAuth();
  const PAGE_SIZE = 25;
  const [inspections, setInspections] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failedCount, setFailedCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [linkedFilter, setLinkedFilter] = useState('all'); // 'unlinked' | 'linked' | 'all'
  const [density, setDensity] = useDensity('inspections_density');
  const [groupBy, setGroupBy] = useState('recent');
  const { sortKey, sortDir, toggleSort } = useSortState();
  const [searchQuery, setSearchQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { formatDateTime, formatDate } = useSettings();

  // Selected date pill → explicit ISO bounds, computed in the browser's local
  // timezone (same as the old client-side date-fns logic) then sent to the
  // backend so "today"/"this week" match what the user sees.
  const dateRange = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case 'today':
        return { from: startOfDay(now), to: endOfDay(now) };
      case 'this_week':
        return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'last_week':
        return { from: startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }) };
      case 'this_month':
        return { from: startOfMonth(now), to: endOfMonth(now) };
      case 'last_month':
        return { from: startOfMonth(subMonths(now, 1)), to: endOfMonth(subMonths(now, 1)) };
      default:
        return null;
    }
  }, [dateFilter]);

  const buildParams = (nextOffset) => {
    const params = { limit: PAGE_SIZE, offset: nextOffset };
    if (searchQuery.trim()) params.search = searchQuery.trim();
    if (statusFilter !== 'all') params.status = statusFilter;
    if (linkedFilter !== 'all') params.linked = linkedFilter;
    if (dateRange) {
      params.date_from = dateRange.from.toISOString();
      params.date_to = dateRange.to.toISOString();
    }
    if (sortKey) { params.sort = sortKey; params.sort_dir = sortDir; }
    return params;
  };

  const loadInspections = async (reset = true) => {
    const nextOffset = reset ? 0 : offset + PAGE_SIZE;
    reset ? setLoading(true) : setLoadingMore(true);
    try {
      const response = await inspectionsApi.getAll(buildParams(nextOffset));
      const data = response.data;
      setTotal(data.total);
      setOffset(nextOffset);
      setInspections((prev) => (reset ? data.items : [...prev, ...data.items]));
    } catch (error) {
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Global failed count for the subtitle (independent of the active filters).
  const loadFailedCount = async () => {
    try {
      const r = await inspectionsApi.getAll({ status: 'failed', limit: 1, offset: 0 });
      setFailedCount(r.data.total);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    loadFailedCount();
  }, []);

  // Re-query from the server whenever any filter changes (debounced so typing
  // in the search box doesn't fire a request per keystroke).
  useEffect(() => {
    const t = setTimeout(() => loadInspections(true), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, dateFilter, linkedFilter, sortKey, sortDir]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDateFilter('all');
    setLinkedFilter('all');
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || dateFilter !== 'all' || linkedFilter !== 'all';
  const hasMore = inspections.length < total;

  const getStatusBadge = (status) => {
    const passed = status === 'passed';
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${
          passed
            ? 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]'
            : 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]'
        }`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${passed ? 'bg-[#047857]' : 'bg-[#DC2626]'}`} />
        {passed ? 'Passed' : 'Failed'}
      </span>
    );
  };

  const openViewDrawer = (inspection) => {
    setSelectedInspection(inspection);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedInspection(null);
  };

  const handleDelete = async () => {
    if (!selectedInspection) return;
    setDeleting(true);
    try {
      await inspectionsApi.delete(selectedInspection.id);
      toast.success('Inspection deleted');
      setConfirmDeleteOpen(false);
      closeDrawer();
      loadInspections(true);
      loadFailedCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete inspection');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="inspections-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Inspections
          </h1>
          <p className="text-muted-foreground">
            View inspection history and results
            {failedCount > 0 && (
              <span className="text-[#B91C1C] font-medium"> · {failedCount} failed</span>
            )}
          </p>
        </div>
        <Button onClick={() => navigate('/scanner')} className="btn-industrial" data-testid="new-inspection-btn">
          <ClipboardCheck className="w-5 h-5 mr-2" />
          New Inspection
        </Button>
      </div>

      {/* Filters + list — single card (gold standard) */}
      <Card className="overflow-hidden">
        <div className="p-4 space-y-4 border-b border-border">
          {/* Row 1 — Search (with total pill), status + work-order dropdowns, view toggle */}
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Inspector, template or asset"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-20"
                data-testid="inspection-search-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-[#8A857A] pointer-events-none">
                {total} total
              </span>
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="passed">Passed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={linkedFilter} onValueChange={setLinkedFilter}>
              <SelectTrigger className="w-full md:w-44" data-testid="inspection-workorder-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any work order</SelectItem>
                <SelectItem value="unlinked">No work order yet</SelectItem>
                <SelectItem value="linked">On a work order</SelectItem>
              </SelectContent>
            </Select>
            <DensityToggle density={density} onChange={setDensity} />
            <div className="flex items-center gap-0.5 rounded-md bg-secondary p-0.5 shrink-0" data-testid="inspection-groupby-toggle">
              {[{ id: 'recent', label: 'Recent' }, { id: 'by_asset', label: 'By asset' }].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setGroupBy(opt.id)}
                  className={`px-3 h-8 rounded-[6px] text-sm font-medium transition-colors ${groupBy === opt.id ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`inspection-groupby-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2 — Date pills */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8A857A] mr-1">Date</span>
            <div className="flex items-center gap-1 bg-secondary rounded-lg p-1 flex-wrap">
              {DATE_FILTERS.map(filter => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setDateFilter(filter.id)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    dateFilter === filter.id
                      ? 'bg-[#FFD108] text-black'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`date-filter-${filter.id}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="ml-1 inline-flex items-center gap-1 text-xs text-[#8A857A] hover:text-[#1A1A1A] transition-colors"
                data-testid="clear-filters-btn"
              >
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            )}
          </div>
        </div>

        {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : inspections.length === 0 ? (
        <div className="py-16 text-center">
            <ClipboardCheck className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Inspections Found</h3>
            <p className="text-muted-foreground mb-4">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Start by scanning an asset QR code'}
            </p>
            {hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            ) : (
              <Button onClick={() => navigate('/scanner')} data-testid="empty-new-inspection-btn">
                <ClipboardCheck className="w-5 h-5 mr-2" />
                Start Inspection
              </Button>
            )}
          </div>
      ) : (
        /* List View */
          <div className="overflow-x-auto">
            <table className="data-table w-full" data-density={density}>
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4">Inspection</th>
                    <th className="text-left p-4">Asset</th>
                    <SortableTh sortKey="inspector" label="Inspector" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="date" label="Date" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="status" label="Status" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <th className="text-left p-4">Work Order</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(groupBy === 'by_asset'
                    ? [...inspections].sort((a, b) => (a.asset_name || '').localeCompare(b.asset_name || ''))
                    : inspections
                  ).map((inspection, idx, arr) => {
                    const showHeader = groupBy === 'by_asset' && (idx === 0 || arr[idx - 1].asset_id !== inspection.asset_id);
                    return (
                    <React.Fragment key={inspection.id}>
                    {showHeader && (
                      <tr className="bg-[#FBFAF7] border-b border-[#E6E2D9]" data-testid={`inspection-group-${inspection.asset_id}`}>
                        <td colSpan={8} className="px-4 py-2">
                          <span className="font-semibold text-[#2A2A2A] text-[12px]">{inspection.asset_name || 'Unknown asset'}</span>
                          <span className="text-muted-foreground text-[12px]"> · {arr.filter(x => x.asset_id === inspection.asset_id).length} inspections</span>
                        </td>
                      </tr>
                    )}
                    <tr 
                      className="border-b border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                      style={inspection.status === 'failed' ? { boxShadow: 'inset 3px 0 0 #DC2626' } : undefined}
                      onClick={() => openViewDrawer(inspection)}
                      data-testid={`inspection-row-${inspection.id}`}
                    >
                      <td className="p-4">
                        <div>
                          <span className="font-medium">{inspection.template_name || 'Inspection'}</span>
                          {inspection.gps_lat && inspection.gps_lng && (
                            <span className="ml-2 text-muted-foreground">
                              <MapPin className="w-3 h-3 inline" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-muted-foreground">{inspection.asset_name || '-'}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{inspection.inspector_name}</span>
                          {inspection.source === 'public_qr' && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border border-amber-400/40 bg-amber-400/10 text-amber-500"
                              title="Submitted via the public QR-code page"
                              data-testid={`inspection-qr-pill-${inspection.id}`}
                            >
                              <QrCode className="w-3 h-3" />
                              QR
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        {formatDate(inspection.created_at)}
                      </td>
                      <td className="p-4">
                        {getStatusBadge(inspection.status)}
                      </td>
                      <td className="p-4">
                        {inspection.linked_defects_count > 0 ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/work-orders?view=${inspection.linked_work_order_ids[0]}`);
                            }}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                            data-testid={`inspection-row-wo-pill-${inspection.id}`}
                            title={`${inspection.linked_defects_count} defect${inspection.linked_defects_count === 1 ? '' : 's'} from this inspection on work order${inspection.linked_work_order_ids.length === 1 ? '' : 's'}`}
                          >
                            <LinkIcon className="w-3 h-3" />
                            On WO
                          </button>
                        ) : inspection.defects_count > 0 ? (
                          <span className="text-xs font-medium bg-transparent" style={{ color: '#B45309' }} title={`${inspection.defects_count} open defect${inspection.defects_count === 1 ? '' : 's'} not yet on a work order`}>
                            {inspection.defects_count} open defect{inspection.defects_count === 1 ? '' : 's'}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openViewDrawer(inspection); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
          </div>
        )}

        {!loading && inspections.length > 0 && (
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-border">
            <span className="text-xs text-muted-foreground" data-testid="inspection-count-footer">
              Showing {inspections.length} of {total} inspections
            </span>
            {hasMore && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadInspections(false)}
                disabled={loadingMore}
                data-testid="inspections-load-more"
              >
                {loadingMore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Load More ({total - inspections.length} more)
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Slide Drawer for Inspection Details */}
      <SlideDrawer
        open={drawerOpen}
        onOpenChange={closeDrawer}
        title="Inspection Details"
        size="lg"
        className="inspection-sheet-content"
      >
        {selectedInspection && (
          <InspectionDetailView 
            inspection={selectedInspection}
            onClose={closeDrawer}
            onDelete={() => setConfirmDeleteOpen(true)}
            isAdmin={isAdmin()}
          />
        )}
      </SlideDrawer>

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={(o) => { if (!o) setConfirmDeleteOpen(false); }}
        title="Delete inspection?"
        description="This inspection record will be permanently removed from the asset's compliance history."
        footnote="Defects raised from this inspection remain in your defect register. This action cannot be undone."
        loading={deleting}
        onConfirm={handleDelete}
        testId="confirm-delete-inspection"
      />
    </div>
  );
};

export const InspectionDetailPage = () => {
  const { id } = useParams();
  const [inspection, setInspection] = useState(null);
  const [template, setTemplate] = useState(null);
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { formatDateTime } = useSettings();

  useEffect(() => {
    loadInspection();
  }, [id]);

  const loadInspection = async () => {
    try {
      const inspectionRes = await inspectionsApi.getById(id);
      setInspection(inspectionRes.data);
      
      const [templateRes, assetRes] = await Promise.all([
        templatesApi.getById(inspectionRes.data.template_id),
        assetsApi.getById(inspectionRes.data.asset_id),
      ]);
      setTemplate(templateRes.data);
      setAsset(assetRes.data);
    } catch (error) {
      toast.error('Failed to load inspection');
      navigate('/inspections');
    } finally {
      setLoading(false);
    }
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'pass': return <Check className="w-5 h-5 text-green-400" />;
      case 'fail': return <X className="w-5 h-5 text-red-400" />;
      default: return <Minus className="w-5 h-5 text-gray-400" />;
    }
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'pass': return 'border-green-900 bg-green-900/20';
      case 'fail': return 'border-red-900 bg-red-900/20';
      default: return 'border-gray-700 bg-gray-800/20';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!inspection) return null;

  const checklistMap = template?.checklist?.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {}) || {};

  return (
    <div className="space-y-8 animate-fade-in" data-testid="inspection-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/inspections')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Inspection Details
            </h1>
            <Badge className={inspection.status === 'passed' ? 'bg-green-900/30 text-green-400 border-green-900' : 'bg-red-900/30 text-red-400 border-red-900'}>
              {inspection.status}
            </Badge>
            {(inspection.flags || []).includes('unusual_hours_reading') && (
              <Badge
                className="bg-amber-900/30 text-amber-400 border-amber-900"
                title="A measurement on this inspection was unusually high. Review before relying on it."
              >
                ⚠ Unusual reading
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Performed by {inspection.inspector_name} on {formatDateTime(inspection.created_at)}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="uppercase tracking-wide">Asset Information</CardTitle>
          </CardHeader>
          <CardContent>
            {asset && (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Asset</span>
                  <span className="font-medium">{asset.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Serial</span>
                  <span className="font-medium">{asset.serial_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Template</span>
                  <span className="font-medium">{template?.name || 'Unknown'}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="uppercase tracking-wide">Location & Signature</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {inspection.gps_lat && inspection.gps_lng ? (
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                <span>{inspection.gps_lat.toFixed(6)}, {inspection.gps_lng.toFixed(6)}</span>
              </div>
            ) : (
              <p className="text-muted-foreground">No GPS location recorded</p>
            )}
            {inspection.signature && (
              <div>
                <Label className="text-muted-foreground">Digital Signature</Label>
                <div className="bg-white rounded-lg p-2 mt-2 inline-block">
                  <img src={inspection.signature} alt="Signature" className="h-16" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="uppercase tracking-wide">Checklist Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {inspection.responses.map((response, index) => {
              const item = checklistMap[response.item_id];
              const itemType = item?.item_type || 'pass_fail';
              const isMeasurement = itemType !== 'pass_fail';

              // Pick a display value depending on what kind of item this is.
              let measurementDisplay = null;
              if (itemType === 'hours' || itemType === 'odometer' || itemType === 'number') {
                const unit = item?.unit || (itemType === 'hours' ? 'hrs' : itemType === 'odometer' ? 'km' : '');
                measurementDisplay = response.value_number != null
                  ? `${response.value_number}${unit ? ' ' + unit : ''}`
                  : '—';
              } else if (itemType === 'text') {
                measurementDisplay = response.value_text || '—';
              } else if (itemType === 'select') {
                measurementDisplay = response.value_select || '—';
              } else if (itemType === 'signature') {
                measurementDisplay = response.signature ? 'Signed' : '—';
              }

              return (
                <div
                  key={response.item_id}
                  className={`p-4 rounded-lg border ${isMeasurement ? 'border-border bg-card' : getResultColor(response.result)}`}
                >
                  <div className="flex items-start gap-3">
                    {!isMeasurement && <div className="mt-1">{getResultIcon(response.result)}</div>}
                    <div className="flex-1">
                      <p className="font-medium">{item?.question || `Question ${index + 1}`}</p>
                      {isMeasurement && (
                        <p className="text-sm font-mono text-[#FFD108] mt-1">{measurementDisplay}</p>
                      )}
                      {response.comment && (
                        <p className="text-sm text-muted-foreground mt-1">{response.comment}</p>
                      )}
                      {response.photo && (
                        <img
                          src={response.photo.startsWith('data:') ? response.photo : `data:image/jpeg;base64,${response.photo}`}
                          alt="Inspection"
                          className="mt-2 rounded-lg max-h-40 object-cover"
                        />
                      )}
                    </div>
                    {!isMeasurement && (
                      <Badge className={`uppercase ${
                        response.result === 'pass' ? 'bg-green-900/30 text-green-400' :
                        response.result === 'fail' ? 'bg-red-900/30 text-red-400' :
                        'bg-gray-800 text-gray-400'
                      }`}>
                        {response.result}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {inspection.status === 'failed' && (
        <Card className="border-yellow-900/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
              <h3 className="text-lg font-semibold">Failed Inspection</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              This inspection has failed items that may require attention.
            </p>
            <Button onClick={() => navigate(`/defects/new?asset=${inspection.asset_id}`)} data-testid="create-defect-btn">
              Report Defect
            </Button>
            <Button variant="outline" className="ml-3" onClick={() => navigate(`/defects?asset=${inspection.asset_id}`)} data-testid="view-defects-btn">
              View Defects
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export const PerformInspectionPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('asset');
  const [asset, setAsset] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [responses, setResponses] = useState([]);
  const [gps, setGps] = useState({ lat: null, lng: null });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(assetId ? 2 : 1);
  const signaturePadRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
    getGPS();
  }, [assetId]);

  useEffect(() => {
    if (canvasRef.current && step === 3) {
      signaturePadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
      });
    }
  }, [step]);

  const loadData = async () => {
    try {
      const templatesRes = await templatesApi.getAll();
      setTemplates(templatesRes.data);

      if (assetId) {
        const assetRes = await assetsApi.getById(assetId);
        setAsset(assetRes.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getGPS = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGps({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.log('GPS error:', error);
        }
      );
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    setSelectedTemplate(template);
    // Initialize one response per item, pre-filling text items configured to
    // auto-fill from the current user (so a mechanic doesn't retype their name).
    setResponses(template.checklist.map(item => {
      const r = {
        item_id: item.id,
        result: '',
        value_number: null,
        value_text: '',
        value_select: '',
        comment: '',
        photo: null,
        signature: null,
      };
      if (item.item_type === 'text' && item.prefill_user_field && user) {
        r.value_text = user[item.prefill_user_field] || '';
      }
      return r;
    }));
    setStep(3);
  };

  const handleResponseChange = (itemId, field, value) => {
    setResponses(prev => prev.map(r => 
      r.item_id === itemId ? { ...r, [field]: value } : r
    ));
  };

  const handlePhotoCapture = (itemId, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleResponseChange(itemId, 'photo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    // Type-aware completeness check
    const incomplete = selectedTemplate.checklist.find((item) => {
      if (!item.required) return false;
      const r = responses.find((rr) => rr.item_id === item.id);
      if (!r) return true;
      const t = item.item_type || 'pass_fail';
      if (t === 'pass_fail') return !r.result;
      if (t === 'hours' || t === 'odometer' || t === 'number') return r.value_number == null || r.value_number === '';
      if (t === 'text') return !r.value_text || !r.value_text.trim();
      if (t === 'select') return !r.value_select;
      if (t === 'signature') return !r.signature;
      return false;
    });
    if (incomplete) {
      toast.error(`Please complete: ${incomplete.question}`);
      return;
    }

    setSubmitting(true);

    try {
      let signature = null;
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        signature = signaturePadRef.current.toDataURL();
      }

      const data = {
        asset_id: asset.id,
        template_id: selectedTemplate.id,
        responses: responses.map(r => ({
          item_id: r.item_id,
          // Send every type-specific field; the backend ignores irrelevant ones per item_type.
          result: r.result || null,
          value_number: r.value_number ?? null,
          value_text: r.value_text || null,
          value_select: r.value_select || null,
          comment: r.comment || null,
          photo: r.photo || null,
          signature: r.signature || null,
        })),
        signature,
        gps_lat: gps.lat,
        gps_lng: gps.lng,
      };

      await inspectionsApi.create(data);
      toast.success('Inspection submitted successfully');
      navigate('/inspections');
    } catch (error) {
      toast.error('Failed to submit inspection');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in" data-testid="perform-inspection-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {step === 1 ? 'Select Asset' : step === 2 ? 'Select Template' : 'Perform Inspection'}
          </h1>
          {asset && <p className="text-muted-foreground">{asset.name} - {asset.serial_number}</p>}
        </div>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2">
        {[1, 2, 3].map(s => (
          <div 
            key={s}
            className={`flex-1 h-2 rounded-full ${s <= step ? 'bg-primary' : 'bg-secondary'}`}
          />
        ))}
      </div>

      {/* Step 2: Select Template */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="uppercase tracking-wide">Select Inspection Template</CardTitle>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No templates available. Ask an admin to create one.
              </p>
            ) : (
              <div className="space-y-3">
                {templates.map(template => (
                  <div
                    key={template.id}
                    className="p-4 border border-border rounded-lg hover:border-primary cursor-pointer transition-colors"
                    onClick={() => handleTemplateSelect(template.id)}
                    data-testid={`template-${template.id}`}
                  >
                    <h3 className="font-semibold">{template.name}</h3>
                    {template.description && (
                      <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      {template.checklist?.length || 0} items
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Perform Inspection */}
      {step === 3 && selectedTemplate && (
        <>
          {/* GPS Status */}
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <MapPin className={`w-5 h-5 ${gps.lat ? 'text-green-400' : 'text-muted-foreground'}`} />
              {gps.lat ? (
                <span className="text-sm">GPS: {gps.lat.toFixed(4)}, {gps.lng.toFixed(4)}</span>
              ) : (
                <span className="text-sm text-muted-foreground">GPS location unavailable</span>
              )}
            </CardContent>
          </Card>

          {/* Checklist */}
          <Card>
            <CardHeader>
              <CardTitle className="uppercase tracking-wide">{selectedTemplate.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {selectedTemplate.checklist.map((item, index) => {
                const response = responses.find(r => r.item_id === item.id);
                const itemType = item.item_type || 'pass_fail';
                const isMeasurement = itemType !== 'pass_fail';
                return (
                  <div key={item.id} className="p-4 border border-border rounded-lg" data-testid={`inspection-item-${itemType}`}>
                    <div className="flex items-start gap-2 mb-4">
                      <span className="text-primary font-bold">{index + 1}.</span>
                      <div>
                        <p className="font-medium">{item.question}</p>
                        {item.required && <span className="text-xs text-red-400">Required</span>}
                      </div>
                    </div>

                    {/* Type-aware input — pass_fail keeps the radio buttons; measurement types render appropriate inputs */}
                    {itemType === 'pass_fail' && (
                      <RadioGroup
                        value={response?.result || ''}
                        onValueChange={(v) => handleResponseChange(item.id, 'result', v)}
                        className="flex gap-4 mb-4"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="pass" id={`${item.id}-pass`} />
                          <Label htmlFor={`${item.id}-pass`} className="flex items-center gap-1 text-green-400">
                            <Check className="w-4 h-4" /> Pass
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="fail" id={`${item.id}-fail`} />
                          <Label htmlFor={`${item.id}-fail`} className="flex items-center gap-1 text-red-400">
                            <X className="w-4 h-4" /> Fail
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="na" id={`${item.id}-na`} />
                          <Label htmlFor={`${item.id}-na`} className="flex items-center gap-1 text-gray-400">
                            <Minus className="w-4 h-4" /> N/A
                          </Label>
                        </div>
                      </RadioGroup>
                    )}

                    {(itemType === 'hours' || itemType === 'odometer' || itemType === 'number') && (
                      <div className="flex items-center gap-2 mb-4">
                        <Input
                          type="number"
                          step="0.1"
                          value={response?.value_number ?? ''}
                          onChange={(e) => handleResponseChange(item.id, 'value_number', e.target.value === '' ? null : parseFloat(e.target.value))}
                          placeholder={itemType === 'hours' ? 'Hours' : itemType === 'odometer' ? 'Odometer' : 'Value'}
                          className="font-mono max-w-xs"
                          data-testid={`item-${item.id}-number`}
                        />
                        <span className="text-sm text-muted-foreground">
                          {item.unit || (itemType === 'hours' ? 'hrs' : itemType === 'odometer' ? 'km' : '')}
                        </span>
                      </div>
                    )}

                    {itemType === 'text' && (
                      <Input
                        maxLength={200}
                        value={response?.value_text ?? ''}
                        onChange={(e) => handleResponseChange(item.id, 'value_text', e.target.value)}
                        placeholder="Enter…"
                        className="mb-4"
                        data-testid={`item-${item.id}-text`}
                      />
                    )}

                    {itemType === 'select' && Array.isArray(item.options) && item.options.length > 0 && (
                      <select
                        value={response?.value_select || ''}
                        onChange={(e) => handleResponseChange(item.id, 'value_select', e.target.value)}
                        className="mb-4 w-full max-w-xs h-10 px-3 rounded-lg bg-card border border-border text-sm"
                        data-testid={`item-${item.id}-select`}
                      >
                        <option value="">— Select —</option>
                        {item.options.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    )}

                    {!isMeasurement && (
                      <Textarea
                        placeholder="Add comment (optional)..."
                        value={response?.comment || ''}
                        onChange={(e) => handleResponseChange(item.id, 'comment', e.target.value)}
                        className="mb-3"
                      />
                    )}

                    <div>
                      <Label className="flex items-center gap-2 cursor-pointer">
                        <div className="flex items-center justify-center w-10 h-10 border border-dashed border-border rounded-lg hover:border-primary">
                          <Camera className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {response?.photo ? 'Photo attached' : 'Add photo'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handlePhotoCapture(item.id, e)}
                          className="hidden"
                        />
                      </Label>
                      {response?.photo && (
                        <img src={response.photo} alt="Captured" className="mt-2 rounded-lg max-h-32 object-cover" />
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Signature */}
          <Card>
            <CardHeader>
              <CardTitle className="uppercase tracking-wide flex items-center gap-2">
                <PenTool className="w-5 h-5" />
                Digital Signature
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-lg p-1 mb-3">
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={150}
                  className="w-full border border-gray-200 rounded"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => signaturePadRef.current?.clear()}
                data-testid="clear-signature-btn"
              >
                Clear Signature
              </Button>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button
            className="w-full btn-industrial h-14"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="submit-inspection-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
            {submitting ? 'Submitting...' : 'Submit Inspection'}
          </Button>
        </>
      )}
    </div>
  );
};
